// src/ImpactRecordForm.js
//
// Three dropdowns this time: Beneficiary, Activity, and Staff (for
// recorded_by). Same fetch-on-mount pattern, just three lists instead
// of one.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function ImpactRecordForm({ token, record, onSaved, onCancel }) {
  const [beneficiaryOptions, setBeneficiaryOptions] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);

  const [beneficiary, setBeneficiary] = useState(record?.beneficiary || '');
  const [activity, setActivity] = useState(record?.activity || '');
  const [recordedBy, setRecordedBy] = useState(record?.recorded_by || '');
  const [narrative, setNarrative] = useState(record?.narrative || '');
  const [statusBefore, setStatusBefore] = useState(record?.status_before || '');
  const [statusAfter, setStatusAfter] = useState(record?.status_after || '');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Token ${token}` };
    // Firing off all three fetches at once (not waiting for one to
    // finish before starting the next) - each has its own .then(), so
    // they run in parallel and each fills its own dropdown independently.
    fetch(`${API_BASE_URL}/api/beneficiaries/`, { headers })
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setBeneficiaryOptions(data); });

    fetch(`${API_BASE_URL}/api/activities/`, { headers })
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setActivityOptions(data); });

    fetch(`${API_BASE_URL}/api/staff/`, { headers })
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setStaffOptions(data); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      beneficiary,
      activity,
      recorded_by: recordedBy || null,
      narrative,
      status_before: statusBefore,
      status_after: statusAfter,
    };

    const url = record
      ? `${API_BASE_URL}/api/impact-records/${record.id}/`
      : `${API_BASE_URL}/api/impact-records/`;
    const method = record ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{record ? 'Edit Impact Record' : 'New Impact Record'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiary</label>
        <select value={beneficiary} onChange={(e) => setBeneficiary(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="" disabled>Select a beneficiary...</option>
          {beneficiaryOptions.map((b) => (
            <option key={b.id} value={b.id}>{b.full_name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
        <select value={activity} onChange={(e) => setActivity(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="" disabled>Select an activity...</option>
          {activityOptions.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Recorded By</label>
        <select value={recordedBy} onChange={(e) => setRecordedBy(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Not specified</option>
          {staffOptions.map((s) => (
            <option key={s.id} value={s.id}>{s.full_name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Narrative</label>
        <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows="4" required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Status Before</label>
        <input type="text" value={statusBefore} onChange={(e) => setStatusBefore(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Status After</label>
        <input type="text" value={statusAfter} onChange={(e) => setStatusAfter(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ImpactRecordForm;
